import React from 'react';
import { Helmet } from 'react-helmet-async';

import { Page } from '../../components';
import {
  colours,
  linkStyle,
  paragraphStyle,
  headingStyle,
  HERO_WIDTH,
  HERO_HEIGHT,
  ROOT_URL,
} from './blogTheme';

const META_TITLE = 'Car Loan Terms in Canada: What a Longer Loan Actually Costs';
const META_DESCRIPTION =
  'Stretching a $37,000 car loan from four years to eight drops the payment by $379 a month and ' +
  'adds $7,832 in interest. What Canadian borrowers should ask before signing — the total cost ' +
  'of borrowing, the annual percentage rate, and how long terms produce negative equity.';
const PUBLISHED_DATE = '2026-08-26';
const ARTICLE_PATH = '/blog/car-loan-term-total-cost-canada';

export const ARTICLE_URL = `${ROOT_URL}${ARTICLE_PATH}`;
export const ARTICLE_HEADLINE =
  'The Monthly Payment Is Not the Price—What Stretching a Canadian Car Loan Really Costs';

/**
 * The hero is the article's own arithmetic, drawn.
 *
 * Every figure in it is a straight amortisation of one principal at one rate
 * over five terms — computed, not sourced, which is why it can be stated
 * exactly. The rate is an assumption and the graphic says so; the point being
 * made is what the term alone does, so the rate is held still.
 */
export const HERO_IMAGE_PATH = '/static/images/car-loan-term-total-cost.webp';
export const HERO_IMAGE_URL = `${ROOT_URL}${HERO_IMAGE_PATH}`;
export const HERO_ALT =
  'Chart of a $37,000 car loan at 8.99% over five terms: 48 months costs $921 a month and $7,187 ' +
  'in interest, 60 months $768 and $9,073, 72 months $667 and $11,007, 84 months $595 and ' +
  '$12,989, and 96 months $542 a month and $15,019 in interest. Eight years instead of four is ' +
  '$379 less each month and $7,832 more interest on the same car.';

const tableCell = {
  padding: '14px 16px',
  borderBottom: `1px solid ${colours.border}`,
  fontSize: 16,
  color: colours.textMid,
  lineHeight: 1.6,
  verticalAlign: 'top',
  textAlign: 'right',
};

const tableHeadCell = {
  ...tableCell,
  color: colours.navy,
  fontWeight: 800,
  fontSize: 13.5,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  borderBottom: `2px solid ${colours.border}`,
};

const listStyle = {
  ...paragraphStyle,
  paddingLeft: 22,
  margin: '0 0 24px',
};

/**
 * A $37,000 loan at 8.99%, amortised over five terms.
 *
 * $37,000 is roughly the average new auto loan Equifax Canada reported for the
 * second quarter of 2026. The rate is illustrative and held constant on
 * purpose: this table is about what the term does on its own.
 */
const TERMS = [
  { term: '48 months', payment: '$921', interest: '$7,187', total: '$44,187' },
  { term: '60 months', payment: '$768', interest: '$9,073', total: '$46,073' },
  { term: '72 months', payment: '$667', interest: '$11,007', total: '$48,007' },
  { term: '84 months', payment: '$595', interest: '$12,989', total: '$49,989' },
  { term: '96 months', payment: '$542', interest: '$15,019', total: '$52,019' },
];

const CarLoanTermArticlePage = () => {
  const articleSchema = {
    '@type': 'Article',
    headline: ARTICLE_HEADLINE,
    description: META_DESCRIPTION,
    datePublished: PUBLISHED_DATE,
    dateModified: PUBLISHED_DATE,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': ARTICLE_URL,
    },
    url: ARTICLE_URL,
    image: {
      '@type': 'ImageObject',
      url: HERO_IMAGE_URL,
      width: HERO_WIDTH,
      height: HERO_HEIGHT,
    },
    author: {
      '@type': 'Organization',
      name: 'SkipTheDealerships.ai',
      url: ROOT_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SkipTheDealerships.ai',
      url: ROOT_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${ROOT_URL}/static/images/dealer-portal-preview.png`,
        width: 1200,
        height: 630,
      },
    },
    about: ['Car loans in Canada', 'Cost of borrowing', 'Negative equity', 'Vehicle financing'],
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${ROOT_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${ROOT_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: ARTICLE_HEADLINE, item: ARTICLE_URL },
    ],
  };

  return (
    <Page
      title={META_TITLE}
      description={META_DESCRIPTION}
      author="SkipTheDealerships.ai"
      openGraphType="article"
      published={PUBLISHED_DATE}
      updated={PUBLISHED_DATE}
      schema={[articleSchema, breadcrumbSchema]}
      socialSharing={{
        title: ARTICLE_HEADLINE,
        description: META_DESCRIPTION,
        images1200: [
          { name: 'facebook', url: HERO_IMAGE_URL, width: HERO_WIDTH, height: HERO_HEIGHT },
        ],
        images600: [
          { name: 'twitter', url: HERO_IMAGE_URL, width: HERO_WIDTH, height: HERO_HEIGHT },
        ],
      }}
    >
      <Helmet>
        <title>{META_TITLE}</title>
        <link rel="canonical" href={ARTICLE_URL} />
      </Helmet>
      <main
        style={{
          fontFamily: 'DM Sans, system-ui, sans-serif',
          background: colours.white,
          color: colours.text,
        }}
      >
        <header
          style={{
            background: `linear-gradient(155deg, ${colours.navy} 0%, #173A4E 100%)`,
            padding: 'clamp(72px, 9vw, 118px) 24px clamp(58px, 8vw, 90px)',
          }}
        >
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            {/* Visible trail matching the BreadcrumbList in the structured
                data, so what a crawler is told and what a reader sees agree. */}
            <nav
              aria-label="Breadcrumb"
              style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, fontWeight: 700 }}
            >
              <a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>
                SkipTheDealerships.ai
              </a>
              <span aria-hidden="true" style={{ margin: '0 8px', opacity: 0.5 }}>
                /
              </span>
              <a href="/blog" style={{ color: 'inherit', textDecoration: 'none' }}>
                Blog
              </a>
            </nav>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                margin: '28px 0 18px',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  color: colours.teal,
                  border: '1px solid rgba(26,158,133,0.38)',
                  background: 'rgba(26,158,133,0.12)',
                  borderRadius: 999,
                  padding: '6px 13px',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Financing
              </span>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
                Published August 26, 2026 · 8 min read
              </span>
            </div>
            <h1
              style={{
                color: colours.white,
                fontSize: 'clamp(35px, 5.5vw, 62px)',
                lineHeight: 1.08,
                letterSpacing: '-0.035em',
                margin: '0 0 24px',
                maxWidth: 900,
              }}
            >
              The Monthly Payment Is Not the Price—What Stretching a Canadian Car Loan Really Costs
            </h1>
            <p
              style={{
                maxWidth: 760,
                color: 'rgba(255,255,255,0.72)',
                fontSize: 'clamp(17px, 2vw, 21px)',
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              A longer loan does not make a vehicle cheaper. It makes the payment smaller and the
              car more expensive, and those are not the same thing.
            </p>
          </div>
        </header>

        <figure style={{ maxWidth: 1000, margin: '0 auto', padding: '44px 24px 0' }}>
          <img
            src={HERO_IMAGE_PATH}
            width={HERO_WIDTH}
            height={HERO_HEIGHT}
            alt={HERO_ALT}
            fetchpriority="high"
            decoding="async"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              aspectRatio: `${HERO_WIDTH} / ${HERO_HEIGHT}`,
              borderRadius: 16,
              border: `1px solid ${colours.border}`,
            }}
          />
          <figcaption
            style={{
              fontSize: 14,
              color: colours.textMid,
              lineHeight: 1.6,
              margin: '14px 4px 0',
            }}
          >
            One principal, one rate, five terms. $37,000 is close to the average new auto loan
            Equifax Canada reported for the second quarter of 2026; the 8.99% rate is illustrative
            and held constant so the chart shows what the term does on its own. Figures are a
            straight amortisation, before tax, fees and any add-on products.
          </figcaption>
        </figure>

        <article style={{ maxWidth: 820, margin: '0 auto', padding: '44px 24px 80px' }}>
          <p style={{ ...paragraphStyle, fontSize: 20, color: colours.text, lineHeight: 1.75 }}>
            Ask a Canadian what their car cost and many will answer with a monthly payment. That is
            not an accident of language. It is the number the transaction is organised around—and it
            is the one number that can be improved without making anything cheaper.
          </p>

          <p style={paragraphStyle}>
            Every dollar of that difference is knowable before you sign. Here is the arithmetic, and
            the questions that surface it.
          </p>

          <h2 style={headingStyle}>The same car at five different prices</h2>

          <p style={paragraphStyle}>
            Take $37,000 financed at 8.99%—roughly the average new auto loan Equifax Canada reported
            for the second quarter of 2026. Change nothing except the number of months:
          </p>

          <div style={{ overflowX: 'auto', margin: '0 0 28px' }}>
            <table
              style={{
                width: '100%',
                minWidth: 560,
                borderCollapse: 'collapse',
                background: colours.white,
              }}
            >
              <caption
                style={{
                  captionSide: 'bottom',
                  fontSize: 13.5,
                  color: colours.textMid,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  paddingTop: 12,
                }}
              >
                Illustrative amortisation of $37,000 at 8.99%, before tax, fees and add-ons. Your
                rate, and therefore your totals, will differ.
              </caption>
              <thead>
                <tr>
                  <th style={{ ...tableHeadCell, textAlign: 'left' }}>Term</th>
                  <th style={tableHeadCell}>Monthly payment</th>
                  <th style={tableHeadCell}>Total interest</th>
                  <th style={tableHeadCell}>Total paid</th>
                </tr>
              </thead>
              <tbody>
                {TERMS.map(row => (
                  <tr key={row.term}>
                    <th
                      scope="row"
                      style={{
                        ...tableCell,
                        textAlign: 'left',
                        color: colours.navy,
                        fontWeight: 800,
                      }}
                    >
                      {row.term}
                    </th>
                    <td style={tableCell}>{row.payment}</td>
                    <td style={tableCell}>{row.interest}</td>
                    <td style={tableCell}>{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={paragraphStyle}>
            Eight years instead of four saves $379 every month and costs $7,832 more in interest.
            The more common trade—84 months instead of 60—saves $173 a month and costs $3,916.
          </p>

          <p style={paragraphStyle}>
            Nobody is being deceived by these numbers. They are simply never the numbers under
            discussion, because the conversation is about what fits a monthly budget, and on that
            question the long term always wins.
          </p>

          <h2 style={headingStyle}>The total cost of borrowing is a number you can ask for</h2>

          <p style={paragraphStyle}>
            Canadian lenders do not get to keep this quiet. Cost-of-credit disclosure has been
            harmonised across the provinces and territories, and federally regulated lenders have
            their own disclosure obligations: the borrower is entitled to see the annual percentage
            rate, the term, the payment schedule and the total cost of borrowing expressed as an
            amount, before the agreement is made.
          </p>

          <p style={paragraphStyle}>
            The annual percentage rate is the one to ask for by name. It is not always the same as
            the interest rate: where fees form part of the cost of credit, the APR reflects them and
            the posted interest rate does not. Two offers at “the same rate” can carry different
            APRs, and the APR is the one that answers what the money costs.
          </p>

          <p style={paragraphStyle}>
            So the question that ends the monthly-payment conversation is short:{' '}
            <em>what is the total amount payable over the full agreement?</em> One number,
            disclosed, comparable between offers.
          </p>

          <h2 style={headingStyle}>Long terms and negative equity travel together</h2>

          <p style={paragraphStyle}>
            A vehicle loses value fastest in its early years, while a long loan repays principal
            slowest in exactly that period. The two curves cross, and for a while the borrower owes
            more than the vehicle is worth. That is negative equity, and it is the mechanism—not the
            misfortune—behind most of the trouble.
          </p>

          <p style={paragraphStyle}>
            Return to the 84-month loan above. Four years in, the borrower has paid $28,565 and
            still owes $18,717. That is $47,282 committed on a $37,000 vehicle that is now four
            years old—and four years is precisely when many people start thinking about replacing
            it.
          </p>

          <p style={paragraphStyle}>
            The Financial Consumer Agency of Canada has been warning about this pairing for a
            decade. Its 2016 research on auto finance found that the share of consumers trading in
            while in a negative equity position had risen from 20% in 2010 to 30% in 2015, and
            identified terms beyond six years as a specific risk to Canadian borrowers.
          </p>

          <h2 style={headingStyle}>What happens when the shortfall is rolled forward</h2>

          <p style={paragraphStyle}>
            A buyer who is $6,000 underwater and wants a different vehicle is usually offered the
            obvious solution: the shortfall is added to the new loan. Nothing is written off. The
            new loan is larger than the new car, interest is charged on the whole of it, and the
            starting position on the next vehicle is worse than the last.
          </p>

          <p style={paragraphStyle}>
            FCAC is direct about where that ends—there are limits on how many times a negative
            position can be carried forward before a borrower starts facing restrictions on
            obtaining financing at all. The cycle is finite. It just does not announce which
            transaction was the last one.
          </p>

          <h2 style={headingStyle}>Add-on products are financed too</h2>

          <p style={paragraphStyle}>
            Extended warranties, protection packages and similar products are usually presented as a
            small change to the payment, which is accurate and beside the point. A $2,000 product
            financed at 8.99% over 84 months adds about $32 a month—and $2,702 to the total, because
            $702 of interest is charged on it over seven years.
          </p>

          <p style={paragraphStyle}>
            That is not an argument against buying one. It is an argument for pricing it as $2,702
            rather than as $32, and for deciding whether it is worth that before it is folded into a
            payment where it becomes invisible.
          </p>

          <h2 style={headingStyle}>Arrange the financing before you choose the car</h2>

          <p style={paragraphStyle}>
            Financing arranged through a seller is a product being sold to you, not a service being
            performed for you. The arranger may be compensated for placing the loan, and the rate
            presented is not necessarily the best rate you qualify for—FCAC has been consulting on
            supervisory expectations for exactly this indirect-lending channel.
          </p>

          <p style={paragraphStyle}>
            The defence is unglamorous and works: get a pre-approval from your own bank or credit
            union first. It costs nothing, it tells you what you actually qualify for, and it turns
            the financing conversation into a comparison instead of a negotiation. If the seller
            beats it, take theirs. You will only know that they did if you brought a number with
            you.
          </p>

          <h2 style={headingStyle}>The questions worth asking out loud</h2>

          <ol style={listStyle}>
            <li>What is the vehicle’s price, separate from anything else in this agreement?</li>
            <li>What is the annual percentage rate, and what is the interest rate?</li>
            <li>How many months, and how often are payments taken?</li>
            <li>What is the total cost of borrowing, in dollars?</li>
            <li>What is the total amount payable over the full agreement?</li>
            <li>
              Which optional products are included, what does each cost, and what is each worth?
            </li>
            <li>Is there a penalty for paying the loan out early?</li>
            <li>
              Is there an existing loan on my trade-in, and how much of it is being carried into
              this one?
            </li>
          </ol>

          <p style={paragraphStyle}>
            If a question cannot be answered before signing, that is the answer.
          </p>

          <h2 style={headingStyle}>The same arithmetic applies to a private sale</h2>

          <p style={paragraphStyle}>
            Private buyers borrow too—usually from their own bank or credit union rather than at the
            point of sale—and every number above behaves identically. The advantage is that the
            financing conversation and the vehicle conversation are separated by default, which is
            most of the protection.
          </p>

          <p style={paragraphStyle}>
            The rest of it is documentary. If the vehicle you are financing still carries someone
            else’s loan, the lien travels with the car and becomes your problem after the transfer —
            which is a{' '}
            <a href="/blog/private-car-sale-paperwork-canada" style={linkStyle}>
              separate piece of homework
            </a>{' '}
            worth doing before any money moves. And a{' '}
            <a href="/blog/used-car-prices-easing-canada-2026" style={linkStyle}>
              softening price market
            </a>{' '}
            only helps the buyer if the financing does not quietly take the saving back.
          </p>

          <section
            aria-label="SkipTheDealerships call to action"
            style={{
              background: colours.bg,
              border: `1px solid ${colours.border}`,
              borderRadius: 20,
              padding: 'clamp(28px, 5vw, 46px)',
              marginTop: 56,
            }}
          >
            <h2 style={{ ...headingStyle, margin: '0 0 14px' }}>Put verification before payment</h2>
            <p style={{ ...paragraphStyle, marginBottom: 28 }}>
              SkipTheDealerships.ai is being built around a more transparent approach: helping
              Canadians connect directly, compare vehicle information and make decisions without
              allowing the transaction to outrun verification.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a
                href="/s"
                style={{
                  display: 'inline-block',
                  background: colours.teal,
                  color: colours.white,
                  fontWeight: 800,
                  padding: '14px 24px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                Browse vehicles
              </a>
              <a
                href="/l/new"
                style={{
                  display: 'inline-block',
                  background: colours.white,
                  color: colours.navy,
                  border: `1px solid ${colours.border}`,
                  fontWeight: 800,
                  padding: '14px 24px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                Sell your vehicle
              </a>
              <a
                href="/leasing-financing"
                style={{
                  display: 'inline-block',
                  background: colours.white,
                  color: colours.navy,
                  border: `1px solid ${colours.border}`,
                  fontWeight: 800,
                  padding: '14px 24px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                Leasing &amp; financing
              </a>
            </div>
          </section>

          <section
            aria-label="Article sources"
            style={{ borderTop: `1px solid ${colours.border}`, marginTop: 54, paddingTop: 30 }}
          >
            <h2 style={{ ...headingStyle, fontSize: 22, margin: '0 0 16px' }}>Sources</h2>
            <ul style={{ paddingLeft: 22, margin: 0, color: colours.textMid, lineHeight: 1.8 }}>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/loans/financing-car.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Financing a car
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/loans/financing-car/risks.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Financial risks when buying a car
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/loans/financing-car/shop-around.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Shopping around for auto-financing
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/news/2016/03/fcac-releases-research-report-highlighting-risks-of-extended-term-car-loans-for-canadian-consumers.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Risks of extended-term car loans (2016
                  research, <em>Auto Finance: Market Trends</em>)
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/rights-responsibilities/rights-credit-loans/rights-personal-loans.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Personal loans — know your rights
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/industry/consultations/indirect-auto-loans-proposed-bulletin.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Financial Consumer Agency of Canada: Consultation on the proposed bulletin on
                  indirect auto loans
                </a>
              </li>
              <li style={{ marginBottom: 10 }}>
                <a
                  href="https://ised-isde.canada.ca/site/consumer-measures-committee/en/formal-agreements/agreement-harmonization-cost-credit-disclosure-laws-canada"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Consumer Measures Committee: Agreement for Harmonization of Cost of Credit
                  Disclosure Laws in Canada
                </a>
              </li>
              <li>
                <a
                  href="https://www.autoremarketing.com/arcanada/equifax-canada-despite-overall-debt-rise-consumers-holding-off-vehicle-purchases"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Equifax Canada quarterly credit trends, reported by Auto Remarketing Canada
                </a>
              </li>
            </ul>
            <p style={{ ...paragraphStyle, fontSize: 14, margin: '22px 0 0' }}>
              This article is general information, not financial advice. The payment and interest
              figures are illustrative amortisations at a stated rate, not an offer, a quote or a
              prediction of the rate any borrower will be given. Rates, terms and eligibility differ
              by lender and by borrower.
            </p>
          </section>
        </article>

        <footer style={{ background: colours.navy, padding: '26px 24px', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: 12, margin: 0 }}>
            © SkipTheDealerships.ai — Drivana Technologies Inc. · A technology marketplace, not a
            dealership, broker or lender.
          </p>
        </footer>
      </main>
    </Page>
  );
};

export default CarLoanTermArticlePage;
